import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import NotesButton from "../components/HouseholdNotes";
import { Plus, Target, Trash2, Pencil, PiggyBank, Info } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { budgetModel, goalProjection, goalTimeline } from "../lib/finance";
import { MilestoneTimeline } from "../components/DebtCountdown";
import { ShareButton, ShareDialog, goalShareInfo } from "../components/ShareCard";
import MilestoneBurst from "../components/MilestoneBurst";
import { fmt, fmt0, monthLabel, uid, round2 } from "../lib/format";
import { PageHeader, Section, Banner, NativeSelect, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const TYPES = ["Emergency fund", "Vacation", "Home repairs", "Vehicle purchase", "Retirement", "Debt elimination", "Major purchase", "Other"];

function GoalDialog({ goal, onClose, onMilestone }) {
  const { update, setGoalSaved } = useStore();
  const [f, setF] = useState(goal || { name: "", type: "Emergency fund", target: "", current: 0, monthly: "", deadline: "" });
  const save = () => {
    if (!f.name || !(Number(f.target) > 0)) return toast.error("Name and target amount required");
    const data = { ...f, target: Number(f.target), current: Number(f.current || 0), monthly: Number(f.monthly || 0) };
    if (goal) {
      const { current, milestones, milestoneDates, ...rest } = data;
      const hit = setGoalSaved(goal.id, current, "Edit goal", rest);
      if (hit) onMilestone(hit);
    } else {
      const id = uid();
      update("Add goal", (d) => d.goals.push({ id, ...data, milestones: [25, 50, 75, 100].filter((m) => data.target > 0 && (data.current / data.target) * 100 >= m) }));
    }
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="goal-dialog">
        <DialogHeader><DialogTitle>{goal ? "Edit goal" : "New savings goal"}</DialogTitle><DialogDescription>Set a target and a monthly amount.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Goal type</Label><NativeSelect value={f.type} onChange={(v) => setF({ ...f, type: v, name: f.name || v })} className="w-full" testid="goal-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</NativeSelect></div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="goal-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target amount</Label><Input type="number" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="goal-target-input" /></div>
            <div><Label>Saved so far</Label><Input type="number" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="goal-current-input" /></div>
            <div><Label>Monthly contribution</Label><Input type="number" value={f.monthly} onChange={(e) => setF({ ...f, monthly: e.target.value })} data-testid="goal-monthly-input" /></div>
            <div><Label>Target date (optional)</Label><Input type="month" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="goal-deadline-input" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="goal-save-button">Save goal</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Goals() {
  const { state, update, setGoalSaved } = useStore();
  const [dialog, setDialog] = useState(null);
  const [contrib, setContrib] = useState({});
  const [burst, setBurst] = useState(null);
  const [shareGoal, setShareGoal] = useState(null);
  const endBurst = useCallback(() => setBurst(null), []);
  const onMilestone = (hit) => {
    setBurst(hit);
    toast.success(hit.milestone === 100 ? `${hit.name} is fully funded — goal complete!` : `${hit.milestone}% of ${hit.name} saved. Keep going!`, { action: { label: "Share", onClick: () => { setBurst(null); setShareGoal(hit.id); } } });
  };
  const model = useMemo(() => budgetModel(state), [state]);
  const surplus = Math.max(0, model.summary.net);
  const active = state.goals.filter((g) => !goalProjection(g).done);
  const committed = active.reduce((s, g) => s + Number(g.monthly || 0), 0);
  const perGoal = active.length ? round2((surplus * 0.5) / active.length) : 0;

  return (
    <div data-testid="goals-page">
      <PageHeader title="Savings goals" subtitle="Set a target, choose a monthly amount, and see when you'll get there.">
        <Button className="rounded-full" onClick={() => setDialog({})} data-testid="add-goal-button"><Plus className="h-4 w-4 mr-1" /> New goal</Button>
      </PageHeader>
      {state.transactions.length > 0 && (
        <Banner icon={Info} testid="goal-recommendation">
          {surplus > 0 ? (
            <>Your budget shows about <b>{fmt0(surplus)}/month</b> left after expenses and planned savings. Committing half of it keeps a cushion: roughly <b>{fmt0(perGoal)}/month per goal</b> across {active.length || 1} active goal(s).{committed > surplus && <> You've committed {fmt0(committed)}/month, which is more than your current surplus.</>}</>
          ) : (
            <>Your current budget has no monthly surplus ({fmt0(model.summary.net)}). Trim discretionary spending on the Budget page before adding contributions.</>
          )}
        </Banner>
      )}
      {!state.goals.length ? (
        <Section className="mt-6"><Empty icon={Target} title="No goals yet" text="Emergency fund, vacation, a new car — start with one." testid="goals-empty"><Button onClick={() => setDialog({})}>Create a goal</Button></Empty></Section>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mt-6 stagger">
          {state.goals.map((g) => {
            const p = goalProjection(g);
            return (
              <Section key={g.id} testid={`goal-card-${g.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{g.type}</p>
                    <p className="font-display text-xl font-bold">{g.name}</p>
                  </div>
                  <div className="flex">
                    <Button variant="ghost" size="icon" onClick={() => setDialog(g)} data-testid={`edit-goal-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => update("Delete goal", (d) => (d.goals = d.goals.filter((x) => x.id !== g.id)))} data-testid={`delete-goal-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <p className="num text-2xl font-semibold mt-3" data-testid={`goal-progress-${g.id}`}>{fmt0(g.current)} <span className="text-base text-muted-foreground">of {fmt0(g.target)}</span></p>
                <Progress value={p.pct} className="h-2.5 mt-2" />
                <div className="flex items-end gap-2 mt-3">
                  <MilestoneTimeline items={goalTimeline(g)} id={`goal-${g.id}`} testid={`goal-milestones-${g.id}`} className="flex-1" />
                  <ShareButton goal={g} className="h-7 px-2 text-xs" />
                </div>
                <dl className="grid grid-cols-2 gap-2 text-sm mt-4">
                  <div><dt className="text-xs text-muted-foreground">Monthly</dt><dd className="num">{fmt0(g.monthly)}</dd></div>
                  <div><dt className="text-xs text-muted-foreground">Est. completion</dt><dd className="num" data-testid={`goal-eta-${g.id}`}>{p.done ? "Complete!" : p.date ? monthLabel(p.date) : "Set a contribution"}</dd></div>
                  <div><dt className="text-xs text-muted-foreground">Still needed</dt><dd className="num">{fmt0(p.remaining)}</dd></div>
                  <div><dt className="text-xs text-muted-foreground">Suggested</dt><dd className="num">{perGoal ? `${fmt0(perGoal)}/mo` : "—"}</dd></div>
                </dl>
                {p.behind && <p className="text-xs text-warning mt-2">At this pace you'll miss your {monthLabel(g.deadline)} target.</p>}
                <NotesButton kind="goals" id={g.id} className="mt-3 -ml-2" />
                <div className="flex gap-2 mt-4">
                  <Input type="number" placeholder="Add savings" value={contrib[g.id] || ""} onChange={(e) => setContrib({ ...contrib, [g.id]: e.target.value })} className="h-9" data-testid={`goal-contribute-input-${g.id}`} />
                  <Button size="sm" variant="outline" onClick={() => { const v = Number(contrib[g.id]); if (!v) return; const hit = setGoalSaved(g.id, round2(Number(g.current) + v), "Add to goal"); setContrib({ ...contrib, [g.id]: "" }); if (hit) onMilestone(hit); else toast.success(`Added ${fmt(v)} to ${g.name}`); }} data-testid={`goal-contribute-button-${g.id}`}><PiggyBank className="h-4 w-4 mr-1" /> Add</Button>
                </div>
              </Section>
            );
          })}
        </div>
      )}
      {dialog && <GoalDialog goal={dialog.id ? dialog : null} onClose={() => setDialog(null)} onMilestone={onMilestone} />}
      {burst && <MilestoneBurst info={burst} onDone={endBurst} verb="saved" />}
      {shareGoal && state.goals.find((x) => x.id === shareGoal) && <ShareDialog info={goalShareInfo(state.goals.find((x) => x.id === shareGoal))} onClose={() => setShareGoal(null)} />}
    </div>
  );
}
